import { X, Copy, Search, CheckCircle } from 'lucide-react'
import type { ApiPermissionOption, User } from './types'

interface CreateApiKeyModalProps {
  createdApiKey: string | null
  keyName: string
  onKeyNameChange: (value: string) => void
  selectedPermissions: string[]
  onPermissionsChange: (value: string[]) => void
  expirationDate: string
  onExpirationDateChange: (value: string) => void
  availablePermissions: ApiPermissionOption[]
  userSearch: string
  onUserSearchChange: (value: string) => void
  searchResults: User[]
  onSearchResultsChange: (value: User[]) => void
  isSearching: boolean
  selectedUser: User | null
  onSelectedUserChange: (value: User | null) => void
  searchUsers: (query: string) => void
  generateApiKeyName: (user: User, permissions?: string[]) => string
  copyApiKey: (key: string) => void
  isCreating: boolean
  onCancel: () => void
  onSubmit: () => void
}

/** API 키 생성. 생성 직후에는 키 값을 한 번만 보여 준다 */
export default function CreateApiKeyModal({
  createdApiKey,
  keyName,
  onKeyNameChange,
  selectedPermissions,
  onPermissionsChange,
  expirationDate,
  onExpirationDateChange,
  availablePermissions,
  userSearch,
  onUserSearchChange,
  searchResults,
  onSearchResultsChange,
  isSearching,
  selectedUser,
  onSelectedUserChange,
  searchUsers,
  generateApiKeyName,
  copyApiKey,
  isCreating,
  onCancel,
  onSubmit,
}: CreateApiKeyModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="m-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {createdApiKey ? 'API 키 생성 완료' : 'API 키 생성'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {createdApiKey ? (
            // API 키 생성 완료 화면
            <div className="text-center">
              <CheckCircle className="mx-auto mb-4 h-16 w-16 text-green-500" />
              <h3 className="mb-2 text-lg font-medium text-gray-900 dark:text-white">
                API 키가 성공적으로 생성되었습니다!
              </h3>
              <p className="mb-4 text-sm text-red-600 dark:text-red-400">
                ⚠️ 보안상 이 API 키는 지금만 표시됩니다. 안전한 곳에
                저장해주세요.
              </p>

              <div className="mb-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                <div className="flex items-center justify-between">
                  <code className="break-all font-mono text-sm text-gray-900 dark:text-gray-100">
                    {createdApiKey}
                  </code>
                  <button
                    onClick={() => copyApiKey(createdApiKey)}
                    className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="rounded-lg bg-blue-50 p-4 text-left dark:bg-blue-900/20">
                <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
                  API 키 정보:
                </h4>
                <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                  <li>
                    <strong>사용자:</strong> {selectedUser?.email}
                  </li>
                  <li>
                    <strong>키 이름:</strong> {keyName}
                  </li>
                  <li>
                    <strong>권한:</strong> {selectedPermissions.join(', ')}
                  </li>
                  {expirationDate && (
                    <li>
                      <strong>만료일:</strong> {expirationDate}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          ) : (
            // API 키 생성 폼
            <div className="space-y-6">
              {/* 사용자 검색 */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  사용자 선택 *
                </label>
                {selectedUser ? (
                  <div className="flex items-center justify-between rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {selectedUser.name || selectedUser.email}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedUser.email} - {selectedUser.plan} 플랜
                      </div>
                    </div>
                    <button
                      onClick={() => onSelectedUserChange(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={userSearch}
                        onChange={e => {
                          onUserSearchChange(e.target.value)
                          searchUsers(e.target.value)
                        }}
                        placeholder="이메일 또는 이름으로 사용자 검색..."
                        className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    {searchResults.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-700">
                        {searchResults.map(user => (
                          <button
                            key={user._id}
                            onClick={() => {
                              onSelectedUserChange(user)
                              onKeyNameChange(generateApiKeyName(user)) // 자동 키 이름 생성
                              onUserSearchChange('')
                              onSearchResultsChange([])
                            }}
                            className="w-full border-b border-gray-100 px-4 py-3 text-left last:border-b-0 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-600"
                          >
                            <div className="font-medium text-gray-900 dark:text-white">
                              {user.name || user.email}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email} - {user.plan} 플랜
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    {isSearching && (
                      <div className="absolute left-0 right-0 top-full mt-1 rounded-lg border border-gray-300 bg-white p-4 text-center dark:border-gray-600 dark:bg-gray-700">
                        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Growth 플랜 이상의 사용자만 API 키를 생성할 수 있습니다.
                </p>
              </div>

              {/* 키 이름 */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  API 키 이름 *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={keyName}
                    onChange={e => onKeyNameChange(e.target.value)}
                    placeholder="예: Production API Key"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                  {selectedUser && (
                    <button
                      type="button"
                      onClick={() =>
                        onKeyNameChange(generateApiKeyName(selectedUser))
                      }
                      className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700 hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                      title="자동 생성"
                    >
                      🔄
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  사용자 선택 시 자동으로 생성됩니다. 수정 가능합니다.
                </p>
              </div>

              {/* 권한 선택 */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  권한 선택 *
                </label>
                <div className="space-y-2">
                  {availablePermissions.map(permission => (
                    <label key={permission.id} className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={e => {
                          let newPermissions
                          if (e.target.checked) {
                            newPermissions = [
                              ...selectedPermissions,
                              permission.id,
                            ]
                          } else {
                            newPermissions = selectedPermissions.filter(
                              p => p !== permission.id
                            )
                          }
                          onPermissionsChange(newPermissions)

                          // 권한 변경 시 API 키 이름 자동 업데이트
                          if (selectedUser && newPermissions.length > 0) {
                            onKeyNameChange(
                              generateApiKeyName(selectedUser, newPermissions)
                            )
                          }
                        }}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="ml-2">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {permission.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {permission.description}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  💡 권한을 선택하면 API 키 이름이 자동으로 업데이트됩니다.
                </p>
              </div>

              {/* 만료일 (선택사항) */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  만료일 (선택사항)
                </label>
                <input
                  type="date"
                  value={expirationDate}
                  onChange={e => onExpirationDateChange(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  비워두면 만료되지 않는 키가 생성됩니다.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            {createdApiKey ? '닫기' : '취소'}
          </button>
          {!createdApiKey && (
            <button
              onClick={onSubmit}
              disabled={isCreating || !selectedUser || !keyName.trim()}
              className="rounded-lg border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isCreating ? '생성 중...' : 'API 키 생성'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
